import type { MouseEventHandler } from 'react'
import { useEffect, useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchNotificationsForSlug, dismissNotificationBanner } from 'actions/ContentActions'
import { SITEWIDE_NOTIFICATION_SLUG } from 'constants/content'
import Image from 'components/Common/Image'
import GeoContext from 'contexts/geoContext'
import PageBanner from 'LuxKit/Banners/PageBanner'
import CookiesContext from 'contexts/CookiesContext'
import TextLink from 'LuxKit/TextLink'
import { FormattedMessage } from 'react-intl'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { SOCIETE_DOUBLE_POINTS_TAKEOVER_ID } from 'takeovers/societeDoublePointsTakeover/constants/societeDoublePointsTakeoverConstants'
import { LUX_PLUS_EXPERIENCE_DISCOUNT_TAKEOVER_ID } from 'takeovers/luxplusExperienceDiscountTakeover/constants/luxPlusExperienceDiscountTakeoverConstants'

interface Props {
  notificationBanner: App.NotificationBanner | null
  slug?: string
  overrideBanner?: App.NotificationBanner | ((regionCode: string) => App.NotificationBanner)
}

function NotificationBanner(props: Props) {
  const {
    slug = SITEWIDE_NOTIFICATION_SLUG,
    notificationBanner,
    overrideBanner,
  } = props
  const dispatch = useAppDispatch()

  const { getCookie } = useContext(CookiesContext)
  const geo = useContext(GeoContext)
  const [isDismissed, setIsDismissed] = useState(false)
  const currentTakeover = useCurrentSiteTakeover()
  const shouldHideForTakeover = currentTakeover?.id === SOCIETE_DOUBLE_POINTS_TAKEOVER_ID ||
    currentTakeover?.id === LUX_PLUS_EXPERIENCE_DISCOUNT_TAKEOVER_ID

  const banner = useMemo(() => {
    if (overrideBanner) {
      return typeof overrideBanner === 'function'
        ? overrideBanner(geo.currentRegionCode)
        : overrideBanner
    }

    return notificationBanner
  }, [geo.currentRegionCode, notificationBanner, overrideBanner])

  const effectiveSlug = banner?.slug || slug

  useEffect(() => {
    // Don't fetch from CMS if we have an override banner
    if (!overrideBanner && effectiveSlug) {
      dispatch(fetchNotificationsForSlug(effectiveSlug, getCookie))
    }
  }, [dispatch, effectiveSlug, getCookie, overrideBanner])

  const onDismiss = useCallback(() => {
    if (banner) {
      dispatch(dismissNotificationBanner(effectiveSlug, banner.bannerText))
      setIsDismissed(true)
    }
  }, [banner, dispatch, effectiveSlug])

  const handleLinkClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((e) => {
    if (banner?.openInPopup) {
      e.preventDefault()

      window.open(banner.linkTo, 'popup', 'width=640,height=640')
    }
  }, [banner])

  if (isDismissed || !banner?.show || shouldHideForTakeover) {
    return null
  }

  const { openInNewTab, openInPopup, bannerText, linkText, linkTo, image } = banner
  const isExternalLink = linkTo?.startsWith('https://')

  return <PageBanner
    id="notification-page-banner"
    className="no-print"
    data-testid="notification-page-banner"
    kind="promotional-primary"
    icon={image ? <div><Image id={image.id} width={image.width} alt={image.alt} enhancedFormats={false} quality="best" /></div> : undefined}
    onDismiss={onDismiss}
    tabletAlign="center"
  >
    {linkTo && linkText && <FormattedMessage
      id="geNCXQaUzFx8F-GdReTr"
      defaultMessage="{bannerText} <link>{linkText}</link>"
      description="Notification banner text with optional link in notifications"
      values={{
        bannerText,
        linkText,
        link: (chunks: React.ReactNode) => <TextLink
          rel="noopener"
          size="medium"
          target={openInNewTab ? '_blank' : (openInPopup ? 'popup' : undefined)}
          onClick={handleLinkClick}
          to={!isExternalLink ? linkTo : undefined}
          href={isExternalLink ? linkTo : undefined}
        >
          {chunks}
        </TextLink>,
      }}
    />}
    {!(linkTo && linkText) && bannerText}
  </PageBanner>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    notificationBanner: state.content.notifications[ownProps.slug || SITEWIDE_NOTIFICATION_SLUG],
  }
}

export default connect(mapStateToProps)(NotificationBanner)
